function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { spacing, themeLight, radius } from '@naf/theme';
import { Text, TextVariant } from '@naf/text';
import { Check } from '@styled-icons/material/Check';
import { Remove } from '@styled-icons/material/Remove';
export const Checkbox = /*#__PURE__*/forwardRef(({
  checked = false,
  onChange,
  name,
  label,
  labelSize = TextVariant.ArticleText,
  message,
  error = false,
  disabled = false,
  indeterminate = false,
  className,
  ...rest
}, ref) => {
  const [isChecked, setIsChecked] = useState(checked);
  const [mouseDown, setMouseDown] = useState(false);
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);
  const handleChange = e => {
    setIsChecked(!isChecked);
    if (onChange) {
      onChange({
        name: name,
        value: e.target.checked
      });
    }
  };
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  useEffect(() => {
    document.addEventListener('mousedown', removeOutline);
    return () => document.removeEventListener('mousedown', removeOutline);
  }, []);
  useEffect(() => {
    document.addEventListener('keydown', addOutline);
    return () => document.removeEventListener('keydown', addOutline);
  }, []);
  return /*#__PURE__*/React.createElement(Container, {
    className: className,
    id: mouseDown ? 'mousedown' : ''
  }, /*#__PURE__*/React.createElement(CheckboxWrap, {
    $disabled: disabled
  }, /*#__PURE__*/React.createElement(ButtonWrap, {
    $labelSize: labelSize
  }, /*#__PURE__*/React.createElement(PseudoCheckbox, _extends({
    name: name,
    $checked: isChecked,
    $error: error,
    $disabled: disabled,
    onClick: handleChange,
    "aria-label": "Checkbox",
    ref: ref,
    type: "button"
  }, rest), indeterminate ? /*#__PURE__*/React.createElement(Remove, null) : /*#__PURE__*/React.createElement(Check, null))), /*#__PURE__*/React.createElement(Label, {
    variant: labelSize,
    $disabled: disabled,
    onClick: !disabled ? handleChange : null
  }, label)), message && /*#__PURE__*/React.createElement(Message, {
    variant: TextVariant.Small,
    $error: error
  }, message));
});
export default Checkbox;
const Container = styled.div.withConfig({
  displayName: "checkbox__Container",
  componentId: "sc-1x5zy84-0"
})(["display:inline-flex;flex-direction:column;&#mousedown *:focus{outline:0;}"]);
const ButtonWrap = styled.div.withConfig({
  displayName: "checkbox__ButtonWrap",
  componentId: "sc-1x5zy84-1"
})(["display:inline-flex;width:fit-content;align-items:center;height:", ";"], props => props.$labelSize === TextVariant.BodyText ? '24px' : '28px');
const PseudoCheckbox = styled.button.withConfig({
  displayName: "checkbox__PseudoCheckbox",
  componentId: "sc-1x5zy84-2"
})(["display:inline-flex;justify-content:center;align-items:center;position:relative;padding:0;min-width:", ";width:", ";height:", ";border-radius:", ";box-sizing:border-box;transition:background 0.2 ease;border:2px solid;border-color:", ";background:", ";color:", ";svg{position:absolute;display:", ";font-size:", ";}&:hover{cursor:pointer;}", " ", " ", " ", " ", ""], spacing.space20, spacing.space20, spacing.space20, radius.s, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  $checked
}) => $checked ? 'initial' : 'none', spacing.space20, ({
  $checked
}) => $checked && css(["border-color:", ";background:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active), ({
  $error
}) => $error && css(["border-color:", ";background:", ";color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.errorBackground : themeLight.componentColors.alert.errorBackground), ({
  $error,
  $checked
}) => $error && $checked && css(["border-color:", ";background:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error, ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error), ({
  $disabled
}) => $disabled && css(["border-color:", ";background:", ";color:", ";&:hover{cursor:not-allowed;border-color:", ";}"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default), ({
  $disabled,
  $checked
}) => $disabled && $checked && css(["border-color:", ";background:", ";"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.disabled : themeLight.componentColors.interactiveElement.disabled));
const Label = styled(Text).withConfig({
  displayName: "checkbox__Label",
  componentId: "sc-1x5zy84-3"
})(["padding-left:", ";margin:0;color:", ";&:hover{cursor:pointer;}", ""], spacing.space8, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  $disabled
}) => $disabled && css(["color:", ";&:hover{cursor:not-allowed;}"], ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText));
const Message = styled(Text).withConfig({
  displayName: "checkbox__Message",
  componentId: "sc-1x5zy84-4"
})(["margin:0;margin-left:calc(", " + ", ");color:", ";", ""], spacing.space20, spacing.space8, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  $error
}) => $error && css(["color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error));
const CheckboxWrap = styled.div.withConfig({
  displayName: "checkbox__CheckboxWrap",
  componentId: "sc-1x5zy84-5"
})(["display:inline-flex;align-items:flex-start;width:fit-content;height:fit-content;", ""], ({
  $disabled
}) => !$disabled && css(["&:hover{", "{border-color:", ";}}"], PseudoCheckbox, ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default));
//# sourceMappingURL=checkbox.js.map